import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, CalendarPlus, Loader2, Trash2, Pencil, CalendarCheck, Link as LinkIcon } from "lucide-react";
import { Btn } from "@/components/Btn";
import { api } from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

let cachedStatus = null;

export function CalendarConnect({ onChange, compact = false }) {
  const [status, setStatus] = useState(cachedStatus);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/oauth/calendar/status").then(({ data }) => { cachedStatus = data; setStatus(data); onChange && onChange(data); }).catch(() => setStatus({ configured: false, connected: false }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/login", { params: { origin: window.location.origin } });
      const w = window.open(data.authorization_url, "gcal", "width=520,height=680");
      const t = setInterval(async () => {
        try {
          const { data: st } = await api.get("/oauth/calendar/status");
          if (st.connected) { clearInterval(t); cachedStatus = st; setStatus(st); onChange && onChange(st); toast.success("Google Calendar connected!"); if (w && !w.closed) w.close(); setBusy(false); }
        } catch { /* keep polling */ }
      }, 2000);
      setTimeout(() => { clearInterval(t); setBusy(false); }, 120000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start connection"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? New events won't sync until you reconnect.")) return;
    await api.post("/oauth/calendar/disconnect");
    load();
    toast.success("Google Calendar disconnected");
  };

  if (!status) return null;
  if (!status.configured) {
    return <div data-testid="cal-not-configured" className="text-xs font-body text-charcoal/60 bg-sunny/40 border-2 border-charcoal/20 rounded-xl px-3 py-2">Google Calendar isn't set up yet.</div>;
  }
  if (status.connected) {
    return (
      <div className="inline-flex items-center gap-2 flex-wrap" data-testid="cal-connected">
        <span className="inline-flex items-center gap-1.5 text-xs font-body font-bold bg-green-500 text-cream rounded-full px-3 py-1.5 border-2 border-charcoal"><CalendarCheck className="w-4 h-4" /> Calendar: {status.email || "connected"}</span>
        {!compact && <button onClick={disconnect} className="text-xs font-body font-bold text-charcoal/60 underline" data-testid="cal-disconnect">Disconnect</button>}
      </div>
    );
  }
  return (
    <button onClick={connect} disabled={busy} data-testid="cal-connect"
      className="inline-flex items-center gap-2 text-xs font-body font-bold bg-white rounded-full px-4 py-2 border-2 border-charcoal shadow-brutsm hover:scale-[1.02] transition-transform disabled:opacity-50">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Connect Google Calendar
    </button>
  );
}

export function ScheduleSection({ project, reload }) {
  const [connected, setConnected] = useState(cachedStatus?.connected || false);
  const [type, setType] = useState("quote");
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [duration, setDuration] = useState(30);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const events = project.events || [];

  const defaultTitle = (t) => (t === "quote" ? "Junk Removal Quote — " : "Junk Removal Job — ") + (project.customer_name || "Customer");
  useEffect(() => { if (!editId) setTitle(defaultTitle(type)); /* eslint-disable-next-line */ }, [type, project.customer_name]);

  const addMin = (s, mins) => {
    const d = new Date(s);
    d.setMinutes(d.getMinutes() + mins);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const reset = () => { setStart(""); setEnd(""); setEditId(null); setType("quote"); setDuration(30); setTitle(defaultTitle("quote")); };
  const save = async () => {
    if (!start) { toast.error("Pick a date & time"); return; }
    if (type === "job" && !end) { toast.error("Pick an end time for the job"); return; }
    setBusy(true);
    try {
      const computedEnd = type === "job" ? end : addMin(start, Number(duration) || 30);
      const body = { type, start, end: computedEnd, title: (title || "").trim() };
      if (editId) await api.patch(`/quotes/${project.id}/schedule/${editId}`, body);
      else await api.post(`/quotes/${project.id}/schedule`, body);
      toast.success(editId ? "Updated on Google Calendar" : "Added to Google Calendar!");
      reset();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); } finally { setBusy(false); }
  };
  const editEvent = (ev) => {
    setEditId(ev.id); setType(ev.type); setTitle(ev.title || defaultTitle(ev.type));
    setStart((ev.start || "").slice(0, 16)); setEnd(ev.end ? ev.end.slice(0, 16) : "");
    if (ev.type === "quote" && ev.start && ev.end) setDuration(Math.max(5, Math.round((new Date(ev.end) - new Date(ev.start)) / 60000)));
  };
  const cancelEvent = async (ev) => {
    if (!window.confirm("Remove this event from your Google Calendar?")) return;
    try { await api.delete(`/quotes/${project.id}/schedule/${ev.id}`); toast.success("Removed from Google Calendar"); reload(); } catch { toast.error("Could not remove"); }
  };

  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-3.5 py-2.5 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  return (
    <div className="bg-white border-2 border-charcoal rounded-2xl p-4 shadow-brut" data-testid="schedule-section">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Schedule</h3>
        <CalendarConnect onChange={(s) => setConnected(s.connected)} compact />
      </div>

      {events.length > 0 && (
        <div className="space-y-2 mb-4">
          {events.map((ev) => (
            <div key={ev.id} className="border-2 border-charcoal/15 rounded-xl p-3" data-testid={`event-${ev.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className={`text-[10px] font-body font-bold rounded-full px-2 py-0.5 mr-2 ${ev.type === "quote" ? "bg-teal text-cream" : "bg-coral text-cream"}`}>{ev.type === "quote" ? "Quote visit" : "Job"}</span>
                  <span className="font-body font-bold text-sm">{fmt(ev.start)}{ev.type === "job" && ev.end ? ` – ${new Date(ev.end).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {ev.html_link && <a href={ev.html_link} target="_blank" rel="noreferrer" className="text-charcoal/50 hover:text-charcoal" title="Open in Google Calendar"><CalendarCheck className="w-4 h-4" /></a>}
                  <button onClick={() => editEvent(ev)} className="text-charcoal/60 hover:text-charcoal" data-testid={`event-edit-${ev.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => cancelEvent(ev)} className="text-coral" data-testid={`event-cancel-${ev.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {ev.location && <div className="text-xs font-body text-charcoal/60 mt-1">{ev.location}</div>}
            </div>
          ))}
        </div>
      )}

      {connected ? (
        <div className="bg-cream border-2 border-charcoal rounded-xl p-3 space-y-2">
          <div className="flex gap-2">
            <button onClick={() => setType("quote")} data-testid="sched-type-quote" className={`flex-1 text-xs font-body font-bold rounded-full px-3 py-2 border-2 border-charcoal ${type === "quote" ? "bg-teal text-cream" : "bg-white"}`}>Quote visit</button>
            <button onClick={() => setType("job")} data-testid="sched-type-job" className={`flex-1 text-xs font-body font-bold rounded-full px-3 py-2 border-2 border-charcoal ${type === "job" ? "bg-coral text-cream" : "bg-white"}`}>Job (custom time)</button>
          </div>
          <label className="block text-xs font-body font-bold text-charcoal/70">Event name (shows on Google Calendar)</label>
          <input type="text" className={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event name" data-testid="sched-title" />
          <label className="block text-xs font-body font-bold text-charcoal/70">{type === "job" ? "Starts" : "Date & time"}</label>
          <input type="datetime-local" className={input} value={start} onChange={(e) => setStart(e.target.value)} data-testid="sched-start" />
          {type === "quote" ? (
            <>
              <label className="block text-xs font-body font-bold text-charcoal/70">Length</label>
              <select className={input} value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="sched-duration">
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </>
          ) : (
            <>
              <label className="block text-xs font-body font-bold text-charcoal/70">Ends</label>
              <input type="datetime-local" className={input} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sched-end" />
            </>
          )}
          <Btn variant="primary" size="md" className="w-full" onClick={save} disabled={busy} data-testid="sched-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarPlus className="w-4 h-4 inline mr-1" /> {editId ? "Update event" : "Add to Google Calendar"}</>}
          </Btn>
          {editId && <button onClick={reset} className="w-full text-xs font-body text-charcoal/60 underline" data-testid="sched-cancel-edit">Cancel edit</button>}
        </div>
      ) : (
        <p className="font-body text-sm text-charcoal/60">Connect your Google Calendar above to schedule a quote visit or a job.</p>
      )}
    </div>
  );
}
